"use client";

import React, { useEffect, useMemo, useSyncExternalStore } from "react";
import { showToast } from "@int-traderjoes/ui-components-library";
import ConfirmationModal from "@/components/ConfirmationModal";
import {
  setConfirmationMessage,
  setConfirmationTittle,
  setHighlightedConfirmationMessage,
} from "@/components/DataTable/Datatable.variables";
import useActiveProjectValidation from "@/components/ActiveProjects/validation/useActiveProjectValidation";
import { useLanguageContext } from "@/context/LanguageContext";
import useTranslation from "@/hooks/useTranslation";

/**
 * Open state of the alert. Kept in this file (no context / hook state needed) so that
 * `openValidationAlert()` can be called from anywhere, while `<ShowTabValidationAlert />`
 * is rendered once (see ActiveProjectDetail/staticTabs.tsx).
 */
type AlertState = { open: boolean; message?: string };

let alertState: AlertState = { open: false };
const listeners = new Set<() => void>();

const setAlertState = (next: AlertState) => {
  alertState = next;
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const getSnapshot = () => alertState;
const getServerSnapshot = () => alertState;

/** Opens the alert. Optionally pass a message to override the default text. */
export const openValidationAlert = (message?: string) => setAlertState({ open: true, message });

/** Closes the alert. */
export const closeValidationAlert = () => setAlertState({ open: false });

/**
 * Validation tab id (keys of `validations`) -> `labelKey` of the tab in `@/constants/tabStructure`.
 * The label text itself is `languageText.TAB_TEXT.ACTIVE_PROJECTS[labelKey]` (same as staticTabs.tsx).
 */
const VALIDATION_TAB_LABEL_KEYS: Record<string, string> = {
  compliance: "COMPLIANCE_DOCUMENTS",
  mainDetails: "MAIN_DETAILS",
  recipe: "RECIPE_AND_RAW_MATERIALS",
  allergens: "ALLERGENS_AND_SENSITIVITIES",
  nutrition: "NUTRITION",
  storage: "STORAGE",
  packagingTab: "PACKAGING_TAB",
  otherLabeling: "PACKAGING_AND_LABELLING",
};

/**
 * Returns the tab ids whose validation is not passed.
 * Same rule as `hasTabError`: a tab is invalid unless `validated === true`.
 */
const getInvalidTabs = (data?: Record<string, { validated?: boolean } | undefined> | null): string[] =>
  Object.keys(data ?? {}).filter(tab => data?.[tab]?.validated !== true);

const TAB_NAMES_PLACEHOLDER = "{tabNames}";

const ShowTabValidationAlert: React.FC = () => {
  const { open, message } = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const { validations } = useActiveProjectValidation();
  const { languageText } = useLanguageContext();
  const tabText = languageText?.TAB_TEXT?.ACTIVE_PROJECTS;
  const confirmationBoxMessages = languageText?.MESSAGES?.CONFIRMATION_BOX_MESSAGES;
  const { t: tTitle } = useTranslation(confirmationBoxMessages?.CONFIRM_TITLE || {});
  const { t: tMessage } = useTranslation(confirmationBoxMessages?.CONFIRM_MESSAGE || {});
  const { t: tAction } = useTranslation(confirmationBoxMessages?.CONFIRM_ACTION || {});
  const { t: tToast } = useTranslation(languageText?.MESSAGES?.TOAST_MESSAGES || {});
  const { t: tTab } = useTranslation(tabText || {});
  // Display names of the failed tabs, e.g. ["Main Details", "Other Labeling"].
  const failedTabs = useMemo(
    () => getInvalidTabs(validations).map(tabId => tTab(VALIDATION_TAB_LABEL_KEYS[tabId] ?? tabId)),
    [validations, tTab]
  );

  // No failed tab names to list (and no custom message): show the generic error toast instead of the alert.
  const showAsToast = useMemo(() => open && !message && failedTabs.length <= 0, [open, message, failedTabs]);
  const showAsAlert = useMemo(() => open && !showAsToast, [open, showAsToast]);

  useEffect(() => {
    if (!showAsToast) return;
    showToast({
      type: "error",
      message: tToast("ACTIVE_PROJECTS_CROSS_TAB_MANDATORY_FIELDS_ERROR"),
    });
    closeValidationAlert();
  }, [showAsToast, tToast]);

  // When the alert closes (OK, outside click or the close icon) clear the texts it wrote to the shared
  // confirmation state - the title, the message and the bold failed-tab list - so they don't linger
  // and show up in other confirmation modals.
  useEffect(() => {
    if (!showAsAlert) return;
    return () => {
      setConfirmationTittle("");
      setConfirmationMessage("");
      setHighlightedConfirmationMessage("");
    };
  }, [showAsAlert]);

  // ConfirmationModal reads its title / message from the shared Datatable.variables state.
  // Only set them while the alert is shown so other confirmation modals are not overwritten.
  if (showAsAlert) {
    setConfirmationTittle(tTitle("ACTIVE_PROJECTS_CROSS_TAB_VALIDATION_ALERT_TITLE"));
    if (message) {
      // Custom message.
      setConfirmationMessage(message);
      setHighlightedConfirmationMessage("");
    } else {
      // Message listing the failed tabs. ConfirmationModal renders the highlighted message in bold
      // right after the message, so split the text around {tabNames}: the tab names (plus any text
      // after the placeholder, e.g. the trailing ".") are the highlighted part.
      const [beforeTabNames = "", ...afterTabNames] = tMessage(
        "ACTIVE_PROJECTS_CROSS_TAB_MANDATORY_FIELDS_TABS_ERROR",
        { tabNames: TAB_NAMES_PLACEHOLDER }
      ).split(TAB_NAMES_PLACEHOLDER);
      setConfirmationMessage(beforeTabNames);
      setHighlightedConfirmationMessage(`${failedTabs.join(", ")}${afterTabNames.join("")}`);
    }
  }

  return (
    <ConfirmationModal
      open={showAsAlert}
      onClose={closeValidationAlert}
      onConfirm={closeValidationAlert}
      confirmText={tAction("OK")}
      showCancelButton={false}
    />
  );
};

export default ShowTabValidationAlert;
